import managementApi from './config';
import { setStorageItem, removeStorageItem, getStorageItem, setStorageJSON, removeStorageItem as removeStorage, getStorageJSON, STORAGE_KEYS } from '@/management/storage';
/**
 * Management Authentication API
 *
 * All auth-related API calls for the management module
 */
// Storage key for auth token
const AUTH_TOKEN_KEY = STORAGE_KEYS.AUTH_TOKEN;
/**
 * Sign in to the management system
 */
export const signIn = async (credentials) => {
    try {
        const response = await managementApi.post('/auth/signin', credentials);
        // Store token if provided in response
        if (response.data?.data?.token) {
            const token = response.data.data.token;
            setStorageItem(AUTH_TOKEN_KEY, token);
        }
        // Store organizations data with user data included
        if (response.data?.data?.organizations) {
            const organizationsWithUser = response.data.data.organizations.map(org => ({
                ...org,
                user: response.data?.data?.administrator ? {
                    id: response.data.data.administrator.id,
                    first_name: response.data.data.administrator.first_name,
                    last_name: response.data.data.administrator.last_name
                } : undefined
            }));
            setStorageJSON(STORAGE_KEYS.ORGANIZATIONS, organizationsWithUser);
        }
        // Set default branch_key after login (auto-select default branch)
        if (response.data?.data?.default_branch?.branch_key) {
            setStorageItem(STORAGE_KEYS.SELECTED_SECTION, response.data.data.default_branch.branch_key);
        }
        else if (response.data?.data?.default_branch?.id && response.data?.data?.organizations) {
            // Fallback: find branch_key from organizations if branch_key not in response
            const organizations = response.data.data.organizations;
            const defaultBranchId = response.data.data.default_branch.id;
            for (const org of organizations) {
                const branch = org.branches?.find(b => b.id === defaultBranchId);
                if (branch && branch.branch_key) {
                    setStorageItem(STORAGE_KEYS.SELECTED_SECTION, branch.branch_key);
                    break;
                }
            }
        }
        return response.data;
    }
    catch (error) {
        const axiosError = error;
        throw axiosError.response?.data || error;
    }
};
/**
 * Start signup process
 */
export const signupStart = async (data) => {
    try {
        const response = await managementApi.post('/auth/signup/start', data);
        return response.data;
    }
    catch (error) {
        const axiosError = error;
        throw axiosError.response?.data || error;
    }
};
/**
 * Verify OTP code during signup
 */
export const verifyOtp = async (data) => {
    try {
        const response = await managementApi.post('/auth/signup/verify-otp', data);
        return response.data;
    }
    catch (error) {
        const axiosError = error;
        throw axiosError.response?.data || error;
    }
};
/**
 * Complete signup process
 */
export const signupComplete = async (data) => {
    try {
        const response = await managementApi.post('/auth/signup/complete', data);
        // Store token if provided in response
        if (response.data?.data?.token) {
            const token = response.data.data.token;
            setStorageItem(AUTH_TOKEN_KEY, token);
        }
        // Store organizations data with user data included
        if (response.data?.data?.organizations) {
            const organizationsWithUser = response.data.data.organizations.map(org => ({
                ...org,
                user: response.data?.data?.administrator ? {
                    id: response.data.data.administrator.id,
                    first_name: response.data.data.administrator.first_name,
                    last_name: response.data.data.administrator.last_name
                } : undefined
            }));
            setStorageJSON(STORAGE_KEYS.ORGANIZATIONS, organizationsWithUser);
        }
        // Set default branch_key after signup (auto-select default branch)
        if (response.data?.data?.default_branch?.branch_key) {
            setStorageItem(STORAGE_KEYS.SELECTED_SECTION, response.data.data.default_branch.branch_key);
        }
        else if (response.data?.data?.default_branch?.id && response.data?.data?.organizations) {
            // Fallback: find branch_key from organizations if branch_key not in response
            const organizations = response.data.data.organizations;
            const defaultBranchId = response.data.data.default_branch.id;
            for (const org of organizations) {
                const branch = org.branches?.find(b => b.id === defaultBranchId);
                if (branch && branch.branch_key) {
                    setStorageItem(STORAGE_KEYS.SELECTED_SECTION, branch.branch_key);
                    break;
                }
            }
        }
        return response.data;
    }
    catch (error) {
        const axiosError = error;
        throw axiosError.response?.data || error;
    }
};
/**
 * Initialize application - validate user, organization, and branch
 * Sends selected organization and branch from localStorage to server
 */
export const init = async () => {
    try {
        // Get selected section from localStorage (could be branch_key or accounting/CRM id)
        const selectedSection = getStorageItem(STORAGE_KEYS.SELECTED_SECTION);
        const organizations = getOrganizations();
        // Find organization slug and branch_id from selected section
        // Note: init API only works with branches, not accounting/CRM
        let organizationSlug = null;
        let branchId = null;
        let branchKey = null;
        if (organizations && selectedSection) {
            // Check if selected section is a branch (by branch_key)
            // If it's accounting or CRM, we'll use default branch
            let isBranch = false;
            for (const org of organizations) {
                const branch = org.branches.find(b => b.branch_key === selectedSection);
                if (branch) {
                    // It's a branch
                    organizationSlug = org.slug;
                    branchId = branch.id;
                    branchKey = branch.branch_key;
                    isBranch = true;
                    break;
                }
            }
            // If not a branch (could be accounting/CRM), use default branch
            if (!isBranch) {
                const defaultOrg = organizations.find(org => org.is_default);
                if (defaultOrg) {
                    organizationSlug = defaultOrg.slug;
                    const defaultBranch = defaultOrg.branches.find(b => b.is_default) || defaultOrg.branches[0];
                    if (defaultBranch) {
                        branchId = defaultBranch.id;
                        branchKey = defaultBranch.branch_key;
                    }
                }
            }
        }
        else if (organizations && organizations.length > 0) {
            // Use default organization if no section selected
            const defaultOrg = organizations.find(org => org.is_default) || organizations[0];
            if (defaultOrg) {
                organizationSlug = defaultOrg.slug;
                const defaultBranch = defaultOrg.branches.find(b => b.is_default) || defaultOrg.branches[0];
                if (defaultBranch) {
                    branchId = defaultBranch.id;
                    branchKey = defaultBranch.branch_key;
                }
            }
        }
        // Send init request with organization and branch
        const response = await managementApi.post('/auth/init', {
            organization_slug: organizationSlug,
            branch_id: branchId ? Number(branchId) : null,
            branch_key: branchKey
        });
        // Store organizations data with user data included
        if (response.data?.data?.organizations) {
            const organizationsWithUser = response.data.data.organizations.map(org => ({
                ...org,
                user: response.data?.data?.administrator ? {
                    id: response.data.data.administrator.id,
                    first_name: response.data.data.administrator.first_name,
                    last_name: response.data.data.administrator.last_name
                } : undefined
            }));
            setStorageJSON(STORAGE_KEYS.ORGANIZATIONS, organizationsWithUser);
        }
        return response.data;
    }
    catch (error) {
        const axiosError = error;
        throw axiosError.response?.data || error;
    }
};
/**
 * Logout from the management system
 */
export const logout = async () => {
    try {
        const response = await managementApi.post('/auth/logout');
        // Clear token and organizations from storage
        removeStorageItem(AUTH_TOKEN_KEY);
        removeStorage(STORAGE_KEYS.ORGANIZATIONS);
        return response.data;
    }
    catch (error) {
        // Even if API call fails, clear local token and organizations
        removeStorageItem(AUTH_TOKEN_KEY);
        removeStorage(STORAGE_KEYS.ORGANIZATIONS);
        const axiosError = error;
        throw axiosError.response?.data || error;
    }
};
/**
 * Get current auth token
 */
export const getAuthToken = () => {
    return getStorageItem(AUTH_TOKEN_KEY);
};
/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
    return !!getAuthToken();
};
/**
 * Clear auth token (for manual logout)
 */
export const clearAuth = () => {
    removeStorageItem(AUTH_TOKEN_KEY);
    removeStorage(STORAGE_KEYS.ORGANIZATIONS);
};
/**
 * Get organizations from storage
 */
export const getOrganizations = () => {
    return getStorageJSON(STORAGE_KEYS.ORGANIZATIONS);
};
/**
 * Get user data from organizations (user data is stored within organizations)
 */
export const getUserData = () => {
    const organizations = getOrganizations();
    if (organizations && organizations.length > 0) {
        // Get user data from first organization (user data is the same across all organizations)
        return organizations[0]?.user || null;
    }
    return null;
};
